import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { reportsBaseUrl } from "constant"
import { RootState } from "store/store"
import { handlingErrorResponse } from "utils/HandlingErrorResponse"
import { getAuthToken } from "utils/getAuthToken"
import { IOtReportsContractType, IOtReportsProjectwiseType, IOtReportsSummaryType, IOvertimeDetailsType, IReportsEmployeeOptType } from "../OT/type"
import { IAttendanceReportsResponseType } from '../attendance/type'
import { IInternshipReportsType } from '../internship/types'
import { ILeaveDetailedReportsType, ILeaveEmployeeBalanceReportType, ILeaveEmployeeWiseReportsType, ILeaveReportsType, ILeavesReportsContractType, ILeavesReportsMonthsType } from '../leave/type'
import { ReportsUrlConst, filterEmployeeUrl, leavesBalanceUrl, leavesContractUrl, overtimeContractUrl } from "./const"
import { IAttendanceReportRequestType, IFilterEmployeeRequestType, IIntershipReportRequestType, ILeaveBalanceRequestType, ILeavesContractRequestType,
    ILeavesDetailedReportRequestType, ILeavesEmployeesWiseRequestType, IMonthlyLeavesSummaryRequestType, IOTContractRequestType, 
    IOvertimeDetailsReportRequestType, IOvertimeProjectRequestType, IOvertimeRequestType, IPaginationResponseType, IResponseType } from "./type"


export const ReportsApi = createApi({
    reducerPath: 'reportsApi',
    baseQuery: fetchBaseQuery({
        baseUrl: reportsBaseUrl,
        prepareHeaders: (headers, { getState }) => {
            const token = getAuthToken(getState() as RootState)
            if (token) {
                headers.set('Authorization', `Bearer ${token}`)
            }
            return headers
        },
    }),
    tagTypes: ['Reports'],
    endpoints: (builder) => ({
        GetOvertimeSummary: builder.mutation<IResponseType<IOtReportsSummaryType[]>, IOvertimeRequestType>({
            query: userReq => ({
                url: ReportsUrlConst.overtimeUrl,
                method: 'POST',
                body: userReq
            }),
            transformErrorResponse: handlingErrorResponse,
            invalidatesTags: [{ type: 'Reports', id: 'GetOvertimeSummary' }],
        }),
        // GetOvertimeEmployees: builder.query<IResponseType<IReportsEmployeeOptType[]>, void>({
        //     query: () => ReportsUrlConst.overtimeEmployeesUrl,
        //     transformErrorResponse: handlingErrorResponse,
        //     providesTags: [{ type: 'Reports', id: 'GetOvertimeEmployees' }],
        // }),
        GetEmployeesOption: builder.query<IResponseType<IReportsEmployeeOptType[]>, IFilterEmployeeRequestType>({
            query: (recordStatus) => `${filterEmployeeUrl(recordStatus)}`,
            transformErrorResponse: handlingErrorResponse,
            providesTags: [{ type: 'Reports', id: 'GetEmployeesOption' }],
        }),
        GetOvertimeContract: builder.query<IResponseType<IOtReportsContractType[]>, IOTContractRequestType>({
            query: (id) => `${overtimeContractUrl(id)}`,
            transformErrorResponse: handlingErrorResponse,
            providesTags: [{ type: 'Reports', id: 'GetOvertimeContract' }],
        }),
        GetOvertimeProjectwise: builder.mutation<IResponseType<IOtReportsProjectwiseType[]>, IOvertimeProjectRequestType>({
            query: userReq => ({
                url: ReportsUrlConst.overtimeProjectUrl,
                method: 'POST',
                body: userReq
            }),
            transformErrorResponse: handlingErrorResponse,
            invalidatesTags: [{ type: 'Reports', id: 'GetOvertimeProjectwise' }],
        }),
        // GetLeavesEmployees: builder.query<IResponseType<IReportsEmployeeOptType[]>, void>({
        //     query: () => ReportsUrlConst.leavesEmployeesUrl,
        //     transformErrorResponse: handlingErrorResponse,
        //     providesTags: [{ type: 'Reports', id: 'GetLeavesEmployees' }],
        // }),
        GetLeavesYears: builder.query<IResponseType<number[]>, void>({
            query: () => ReportsUrlConst.leavesYearUrl,
            transformErrorResponse: handlingErrorResponse,
            providesTags: [{ type: 'Reports', id: 'GetLeavesYears' }],
        }),
        GetLeavesMonths: builder.query<IResponseType<ILeavesReportsMonthsType[]>, void>({
            query: () => ReportsUrlConst.leavesMonthsUrl,
            transformErrorResponse: handlingErrorResponse,
            providesTags: [{ type: 'Reports', id: 'GetLeavesMonths' }],
        }),
        GetMonthlyLeavesSummary: builder.mutation<IResponseType<ILeaveReportsType[]>, IMonthlyLeavesSummaryRequestType>({
            query: userReq => ({
                url: ReportsUrlConst.leavesMonthlySummaryUrl,
                method: 'POST',
                body: userReq
            }),
            transformErrorResponse: handlingErrorResponse,
            invalidatesTags: [{ type: 'Reports', id: 'GetMonthlyLeavesSummary' }],
        }),
        GetLeavesContract: builder.query<IResponseType<ILeavesReportsContractType[]>, ILeavesContractRequestType>({
            query: (id) => `${leavesContractUrl(id)}`,
            transformErrorResponse: handlingErrorResponse,
            providesTags: [{ type: 'Reports', id: 'GetLeavesContract' }],
        }),
        GetLeavesEmployeeWise: builder.mutation<IResponseType<ILeaveEmployeeWiseReportsType[]>, ILeavesEmployeesWiseRequestType>({
            query: userReq => ({
                url: ReportsUrlConst.leavesEmployeeWiseUrl,
                method: 'POST',
                body: userReq
            }),
            transformErrorResponse: handlingErrorResponse,
            invalidatesTags: [{ type: 'Reports', id: 'GetLeaves' }],
        }),
        GetLeavesDetailedReport: builder.mutation<IResponseType<ILeaveDetailedReportsType[]>, ILeavesDetailedReportRequestType>({
            query: userReq => ({
                url: ReportsUrlConst.leavesDetailedReportUrl,
                method: 'POST',
                body: userReq
            }),
            transformErrorResponse: handlingErrorResponse,
            invalidatesTags: [{ type: 'Reports', id: 'GetLeaves' }],
        }),
        GetLeavesEmployeesBalance: builder.query<IResponseType<ILeaveEmployeeBalanceReportType[]>, ILeaveBalanceRequestType>({
            query: (contractId) =>`${leavesBalanceUrl(contractId)}`,
            transformErrorResponse: handlingErrorResponse,
            providesTags: [{ type: 'Reports', id: 'GetLeavesEmployees' }],
        }),
        GetInternshipReportList: builder.mutation<IResponseType<IInternshipReportsType[]>, IIntershipReportRequestType>({
            query: userReq => ({
                url: ReportsUrlConst.internshipReportUrl,
                method: 'POST',
                body: userReq
            }),
            transformErrorResponse: handlingErrorResponse,
            invalidatesTags: [{ type: 'Reports', id: 'GetInternshipReportList' }],
        }),
        GetOvertimeDetailsReportList: builder.mutation<IResponseType<IOvertimeDetailsType[]>, IOvertimeDetailsReportRequestType>({
            query: userReq => ({
                url: ReportsUrlConst.overtimeDetailstUrl,
                method: 'POST',
                body: userReq
            }),
            transformErrorResponse: handlingErrorResponse,
            invalidatesTags: [{ type: 'Reports', id: 'GetOvertimeDetailsReportList' }],
        }),
        GetAttendanceReportList: builder.mutation<IResponseType<IAttendanceReportsResponseType[]>, IAttendanceReportRequestType>({
            query: userReq => ({
                url: ReportsUrlConst.attendanceReportUrl,
                method: 'POST',
                body: userReq
            }),
            transformErrorResponse: handlingErrorResponse,
            invalidatesTags: [{ type: 'Reports', id: 'GetAttendanceReportList' }],
        }),
    }),
})